import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'

import type { Feature } from '../feature'
import * as gql from '../gql'

/**
 * Loads the dropdown-owned scenario list for the current route-bound app.
 *
 * This intentionally refreshes only {@link Feature} state. The root
 * {@link me.Feature} snapshot is still the navigation/context source, but a
 * dropdown refresh should not rewrite that snapshot because doing so causes
 * global context selectors to emit and can ripple into route-bound app data.
 */
export const refreshScenariosOnRequest = store.effects.create(
  (feature: Feature, meFeature: me.Feature) =>
    feature.actions.refreshScenarios.request.observe().pipe(
      rxjs.withLatestFrom(
        meFeature.contexts.selectors.appContext.asObservable,
      ),
      // Prefer the newest refresh request. Opening the dropdown, then quickly
      // saving/moving/deleting a scenario should leave us with the post-mutation
      // list, not an older overlay-open request.
      rxjs.switchMap(([action, appContext]) => {
        if (!appContext) {
          return rxjs.of(
            feature.actions.refreshScenarios.cancellation
              .from(action)
              .create({
                reason: 'No app context available',
                request: action.data,
              }),
          )
        }

        return gql.queries.scenarios.execute(appContext).pipe(
          rxjs.map(response =>
            feature.actions.refreshScenarios.success.from(action).create({
              request: action.data,
              response: response.items,
            }),
          ),
          rxjs.catchError((error: Error) =>
            rxjs.of(
              feature.actions.refreshScenarios.error.from(action).create({
                error,
                request: action.data,
              }),
            ),
          ),
        )
      }),
    ),
)
